/**
 * @file ActionButton.jsx
 * @description A button component for actions (edit, duplicate).
 * @requires React, PropTypes, semantic-ui-react
 * @exports ActionButton
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Button, Icon, Popup } from 'semantic-ui-react';

import './ActionButtons.css';

/**
 * handleElementAction is a helper function that prevents the default redirection behavior of an element.
 * 
 * @function handleElementAction
 * @param {React.SyntheticEvent} element Element to prevent redirection.
 * @param {Function} action Function to perform the action (edit, duplicate, delete).
 * @param {string} id Element ID to be passed for the respective action.
 * @returns {void}
 * 
 * @example
 * handleElementAction(eventElement.id, handleEditEvent);
 */
const handleElementAction = async (element, action, id) => {
    element.stopPropagation(); // Disables the redirection from the div zone
    action(id);
};

/**
 * @typedef {Object} ActionButtonProps
 * @property {Function} action - Function to perform the action (edit, duplicate).
 * @property {string} elementId - Element ID to be passed for the respective action.
 * @property {string} label - Button aria label.
 * @property {string} icon - Button icon name.
 * @property {string} tooltipText - Tooltip text.
 * @property {string} [color='blue'] - Button color.
 */

/**
 * ActionButton is a reusable component for action buttons (edit, duplicate).
 * 
 * @component
 * @param {ActionButtonProps} props Component properties.
 * @returns {JSX.Element} A styled button component with an action and tooltip.
 * 
 * @example
 * const handleEditEvent = (id) => console.log(`Editing event with ID: ${id}`);
 * 
 * <ActionButton
 *    label="Modify this event"
 *    icon="pencil"
 *    action={handleEditEvent}
 *    elementId={eventElement.id}
 *    tooltipText={'Popup_Edit'}
 * />
 */
const ActionButton = ({
    action,
    elementId,
    label,
    icon,
    tooltipText,
    color = 'blue'
}) => {
    if (!action) {
        throw new Error("ActionButton: Missing required prop 'action'. Expected a function.");
    }

    if (!elementId) {
        throw new Error("ActionButton: Missing required prop 'elementId'. Expected a string identifier.");
    }

    return (
        <Popup
            trigger={
                <Button
                    className="element-buttons"
                    onClick={(element) => handleElementAction(element, action, elementId)}
                    icon
                    color={color}
                    aria-label={label}
                >
                    <Icon name={icon} />
                </Button>
            }
            content={tooltipText}
            on="hover"
        />
    );
};

ActionButton.propTypes = {
    label: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    action: PropTypes.func.isRequired,
    elementId: PropTypes.string.isRequired,
    tooltipText: PropTypes.string.isRequired,
    color: PropTypes.string,
};

export default ActionButton;